import { useParams, Link, useNavigate } from 'react-router-dom';
import './WikiItemDetail.css';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { CraftingGrid, McIcon, slotsToArray } from '../components/CraftingGrid';

const VIDEO = "https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4";

const rarityColors = {
  common: '#9e9e9e',
  uncommon: '#52c078',
  rare: '#5a9ee0',
  epic: '#a070e0',
  legendary: '#f0ab20',
  boss: '#e84c3d',
};

const calculateOneInAAmount = (chance) => {
  if (!chance || chance <= 0) return null;
  if (chance >= 100) return '1 in 1';
  const amount = Math.round(100 / chance);
  return `1 in ${amount}`;
}

function itemMaterial(item) {
  return item?.material || item?.type || 'barrier';
}

// Resolve a slot key → custom item if it matches a slug, null for vanilla keys
function resolveCustomItem(key, items) {
  return items.find(i => i.slug === key) ?? null;
}


function ObtainCard({ label, iconKey, children }) {
  return (
    <div className="wiki-obtain-drop-card">
      <div className="wiki-obtain-drop-head">
        <McIcon materialKey={iconKey} size={20} />
        <span className="wiki-obtain-drop-label">{label}</span>
        <div className="wiki-obtain-drop-rule" />
      </div>
      {children}
    </div>
  );
}

function ObtainEntry({ entry, mobs, material }) {
  if (entry.type === 'drop') {
    const mob = entry.mob_id ? mobs.find(m => String(m.id) === String(entry.mob_id)) : null;
    const mobLabel = mob
      ? <Link to={`/wiki/mob/${mob.id}`} className="wiki-obtain-mob-link">{mob.name}</Link>
      : entry.mob_name
        ? <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-sm)' }}>{entry.mob_name}</span>
        : <span className="wiki-obtain-mob-unknown">Unknown mob</span>;
    return (
      <ObtainCard label="Mob Drop" iconKey={material}>
        <div className="wiki-obtain-drop-row">
          {mobLabel}
          {entry.chance
            ? <span className="wiki-obtain-chance">{entry.chance}% ({calculateOneInAAmount(entry.chance)})</span>
            : <span className="wiki-obtain-chance-unknown">—</span>
          }
        </div>
      </ObtainCard>
    );
  }

  if (entry.type === 'block_drop') {
    return (
      <ObtainCard label="Block Drop" iconKey="grass_block">
        <div className="wiki-obtain-drop-row">
          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-sm)' }}>
            {entry.location || 'Unknown block'}
          </span>
          {entry.chance
            ? <span className="wiki-obtain-chance">{entry.chance}% ({calculateOneInAAmount(entry.chance)})</span>
            : <span className="wiki-obtain-chance-unknown">—</span>
          }
        </div>
      </ObtainCard>
    );
  }

  if (entry.type === 'supply_drop') {
    return (
      <ObtainCard label="Supply Drop" iconKey="chest">
        <div className="wiki-obtain-drop-row">
          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-sm)' }}>
            {entry.location || 'Supply Drop'}
          </span>
          {entry.chance
            ? <span className="wiki-obtain-chance">{entry.chance}% ({calculateOneInAAmount(entry.chance)})</span>
            : <span className="wiki-obtain-chance-unknown">—</span>
          }
        </div>
      </ObtainCard>
    );
  }

  if (entry.type === 'treasure') {
    return (
      <ObtainCard label="Treasure" iconKey="golden_sword">
        <div className="wiki-obtain-drop-row">
          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--t-sm)' }}>
            {entry.location || 'Treasure'}
          </span>
          {entry.chance
            ? <span className="wiki-obtain-chance">{entry.chance}% ({calculateOneInAAmount(entry.chance)})</span>
            : <span className="wiki-obtain-chance-unknown">—</span>
          }
        </div>
      </ObtainCard>
    );
  }

  // Simple row types
  let text;
  if (entry.type === 'craft') text = 'Craftable at crafting table';
  else text = entry.description;

  return (
    <div className="wiki-obtain-row">
      <span className="wiki-obtain-bullet">▸</span>
      <span>{text}</span>
    </div>
  );
}

function parseObtain(raw) {
  try { return JSON.parse(raw || '[]'); } catch { return []; }
}

function parseHistory(raw) {
  try { return JSON.parse(raw || '[]'); } catch { return []; }
}

// ── MC colour-code renderer ───────────────────────────────────
const MC_COLORS = {
  '0':'#000000','1':'#0000AA','2':'#00AA00','3':'#00AAAA',
  '4':'#AA0000','5':'#AA00AA','6':'#FFAA00','7':'#AAAAAA',
  '8':'#555555','9':'#5555FF','a':'#55FF55','b':'#55FFFF',
  'c':'#FF5555','d':'#FF55FF','e':'#FFFF55','f':'#FFFFFF',
};

function parseMcText(text) {
  if (!text) return [{ text: '', color: null, bold: false, italic: false, underline: false, strike: false }];
  const parts = [];
  let color = null, bold = false, italic = false, underline = false, strike = false;
  const re = /§([0-9a-frlonmkA-FRLONMK])/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index), color, bold, italic, underline, strike });
    const c = m[1].toLowerCase();
    if (MC_COLORS[c])    { color = MC_COLORS[c]; bold = false; italic = false; underline = false; strike = false; }
    else if (c === 'l')  bold = true;
    else if (c === 'o')  italic = true;
    else if (c === 'n')  underline = true;
    else if (c === 'm')  strike = true;
    else if (c === 'r')  { color = null; bold = false; italic = false; underline = false; strike = false; }
    last = re.lastIndex;
  }
  if (last < text.length) parts.push({ text: text.slice(last), color, bold, italic, underline, strike });
  return parts;
}

function McText({ text, defaultColor = '#AAAAAA' }) {
  const parts = parseMcText(text);
  return (
    <>
      {parts.map((p, i) => (
        <span key={i} style={{
          color: p.color ?? defaultColor,
          fontWeight: p.bold ? 'bold' : undefined,
          fontStyle: p.italic ? 'italic' : undefined,
          textDecoration: [p.underline && 'underline', p.strike && 'line-through'].filter(Boolean).join(' ') || undefined,
        }}>{p.text}</span>
      ))}
    </>
  );
}

function formatChance(raw) {
  if (!raw && raw !== 0) return null;
  const num = parseFloat(String(raw).replace('%', ''));
  if (isNaN(num) || num <= 0) return String(raw);
  if (num >= 100) return `${num}% (1/1)`;
  return `${num}% (1/${Math.round(100 / num)})`;
}

export default function WikiItemDetail({ items = [], mobs = [] }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const item = items.find(i => String(i.id) === String(id) && i.enabled !== 0 && i.enabled !== '0');

  if (!item) {
    return (
      <div className="container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <h2>Item not found</h2>
        <Link to="/wiki" className="btn btn-secondary" style={{ marginTop: '1rem' }}>Back to Wiki</Link>
      </div>
    );
  }

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <video className="wiki-detail-hero-vid" src={VIDEO} autoPlay muted loop playsInline />
        <div className="wiki-detail-hero-overlay" />
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <button className="wiki-back-btn" onClick={() => navigate(-1)}>← Back</button>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box">
              <McIcon materialKey={itemMaterial(item)} size={48} />
            </div>
            <div>
              <h1 className="wiki-detail-item-name">{item.name}</h1>
              <div
                className="wiki-detail-item-type"
                style={{ color: rarityColors[item.rarity] || 'var(--text-secondary)' }}
              >
                {item.rarity} {item.category}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      <StaggerContainer className="container">
        <div className="wiki-detail-grid">
          <StaggerItem className="wiki-detail-main">
            {item.abilities?.length > 0 && (
            <div>
              <span className="wiki-detail-label">Abilities</span>
              <div className="wiki-ability-list">
                {item.abilities.map((ability, idx) => (
                  <div key={idx} className="wiki-ability-card">
                    <div className="wiki-ability-head">
                      <span className="wiki-ability-name">{ability.name}</span>
                      {ability.type && <span className="wiki-ability-type">{ability.type}</span>}
                    </div>
                    <div className="wiki-ability-desc">{ability.description}</div>
                    {ability.cooldown && (
                      <div className="wiki-ability-cooldown">Cooldown: {ability.cooldown}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            )}

            {(item.description || item.lore) && (
            <div>
              <span className="wiki-detail-label">Other info</span>
              <div className="wiki-detail-box">
                {item.description && (
                  <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{item.description}</p>
                )}
                {item.lore && (
                  <div className={`wiki-lore-box${item.description ? ' wiki-lore-box--spaced' : ''}`}>
                    {item.lore.split('\n').map((line, i) => (
                      <div key={i} className="wiki-lore-line">
                        <McText text={line || ' '} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}
          </StaggerItem>

          <StaggerItem className="wiki-detail-sidebar">
            {/* ── Crafting Recipe ── */}
            <div>
              <span className="wiki-detail-label">Crafting Recipe</span>
              <div className="wiki-detail-box">
                {(() => {
                  const slots = slotsToArray(item.recipe);
                  const hasSlots = slots.some(s => s.key);
                  if (!hasSlots) {
                    return <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)', margin: 0 }}>Non-craftable item.</p>;
                  }

                  // Aggregate ingredient totals
                  const totals = {};
                  slots.forEach(s => {
                    if (!s.key) return;
                    totals[s.key] = (totals[s.key] || 0) + (s.count || 1);
                  });

                  return (
                    <>
                      <CraftingGrid
                        slots={slots}
                        outputKey={itemMaterial(item)}
                        outputCount={item.recipe?.output_count ?? item.recipe_output_count ?? 1}
                        note={item.recipe?.note}
                        items={items}
                      />
                      <div className="wiki-recipe-ingredients">
                        <span className="wiki-recipe-ing-label">Ingredients</span>
                        {Object.entries(totals).map(([key, total]) => {
                          const custom = resolveCustomItem(key, items);
                          const name = custom ? custom.name : key.replace(/_/g, ' ');
                          return (
                            <div key={key} className="wiki-recipe-ing-row">
                              <McIcon materialKey={custom?.material || key} size={18} />
                              {custom ? (
                                <Link
                                  to={`/wiki/item/${custom.id}`}
                                  className="wiki-recipe-ing-name wiki-recipe-ing-link"
                                >
                                  {name}
                                </Link>
                              ) : (
                                <span className="wiki-recipe-ing-name">{name}</span>
                              )}
                              <span className="wiki-recipe-ing-qty">×{total}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* ── Ways to Obtain ── */}
            {(() => {
              const entries = parseObtain(item.obtain);
              if (!entries.length) return null;
              return (
                <div>
                  <span className="wiki-detail-label">Ways to Obtain</span>
                  <div className="wiki-detail-box wiki-obtain-box">
                    {entries.map((entry, i) => (
                      <ObtainEntry key={i} entry={entry} mobs={mobs} material={itemMaterial(item)} />
                    ))}
                  </div>
                </div>
              );
            })()}
          </StaggerItem>
        </div>

        {(() => {
          const history = parseHistory(item.history);
          if (!history.length) return null;
          return (
            <StaggerItem className="wiki-history-section">
              <h3 className="wiki-history-title">Updates</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {history.map((entry, idx) => (
                  <div key={idx} className="wiki-history-row">
                    <span className="wiki-history-version">v{entry.version}</span>
                    <ul className="wiki-history-changes">
                      {(entry.changes ?? []).map((c, ci) => (
                        <li key={ci}>{c}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </StaggerItem>
          );
        })()}
      </StaggerContainer>
    </div>
  );
}
